import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import type { BrandAuthConfig } from "@nextday/brand-auth";
import { useEffect } from "react";

/**
 * Kinde redirect target. KindeProvider auto-detects `?code=&state=` on
 * whichever URL the SDK lands on and exchanges it for tokens. Once
 * `isLoading` flips to false we route the user back to the funnel cursor
 * they were on before the auth round-trip.
 *
 * Cursor restoration relies on `useFunnelDraft` — it re-hydrates from
 * localStorage when the funnel root mounts, so all we do here is navigate
 * to the saved return path. The per-app auth config is also persisted so
 * `FunnelAuthShell` can mount the right `<BrandAuthProvider>` during the
 * callback render (no application context fetch happens at the funnel root).
 */

const SESSION_KEY = "nextday:auth:session";

export type AuthSession = {
  returnPath: string;
  authConfig: BrandAuthConfig;
};

const isAuthSession = (candidate: unknown): candidate is AuthSession => {
  if (typeof candidate !== "object" || candidate === null) return false;
  const value = candidate as Record<string, unknown>;
  if (typeof value.returnPath !== "string") return false;
  const cfg = value.authConfig;
  if (typeof cfg !== "object" || cfg === null) return false;
  const c = cfg as Record<string, unknown>;
  return (
    typeof c.kindeDomain === "string" &&
    typeof c.kindeOrgCode === "string" &&
    typeof c.appOrigin === "string" &&
    typeof c.kindeClientId === "string" &&
    typeof c.connectionIds === "object" &&
    c.connectionIds !== null
  );
};

export const saveAuthSession = (session: AuthSession): void => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // sessionStorage unavailable (private mode, quota). The callback will
    // fall back to the funnel root with no config override.
  }
};

export const readAuthSession = (): AuthSession | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isAuthSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const clearAuthSession = (): void => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
};

/**
 * Detect Kinde's PKCE response on the current URL.
 */
export const isKindeCallbackUrl = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return params.has("code") && params.has("state");
};

export const AuthCallbackPage = () => {
  const { isLoading } = useKindeAuth();

  useEffect(() => {
    if (isLoading) return;
    const session = readAuthSession();
    clearAuthSession();
    const target = session?.returnPath ?? "/";
    window.location.replace(target);
  }, [isLoading]);

  return (
    <div className="flex h-dvh w-full items-center justify-center bg-[hsl(var(--brand-bg))] text-[hsl(var(--brand-bg-foreground))]">
      <div className="flex flex-col items-center gap-3">
        <div className="size-10 animate-spin rounded-full border-2 border-[hsl(var(--brand-bg-foreground)/0.2)] border-t-[hsl(var(--brand-bg-foreground))]" />
        <p className="text-sm opacity-80">Signing you in…</p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
